import { useEffect, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '@atrvd/saas-ui'
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOnExit } from 'react-plaid-link'
import { Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@atrvd/saas-ui'
import { useConnectBank, useCompleteUpdate } from '@/hooks/usePlaid'
import {
  PLAID_TOKEN_KEY,
  PLAID_FACILITY_KEY,
  PLAID_ORG_KEY,
  PLAID_UPDATE_MODE_KEY,
  PLAID_ITEM_ID_KEY,
  clearPlaidOAuthState,
} from '@/lib/plaid-oauth'

/** Timeout in milliseconds to wait for the Plaid SDK to become ready before showing an error */
const SDK_INIT_TIMEOUT_MS = 30_000

/**
 * OAuth callback page for Plaid bank connections.
 * Handles the redirect from the bank's OAuth portal,
 * re-initializes Plaid Link to complete the connection,
 * then navigates back to the facility banking page.
 *
 * Supports two modes:
 * - Initial connect: exchanges public_token for access_token (default)
 * - Update mode (reconnect): calls complete-update to clear error state
 *   (detected via PLAID_UPDATE_MODE_KEY in localStorage)
 */
export function PlaidOAuthCallbackPage() {
  const navigate = useNavigate()
  const connectBank = useConnectBank()
  const completeUpdate = useCompleteUpdate()
  const [error, setError] = useState<string | null>(null)

  // Read localStorage once on mount via lazy initializers
  const [linkToken] = useState(() => localStorage.getItem(PLAID_TOKEN_KEY))
  const [facilityId] = useState(() => localStorage.getItem(PLAID_FACILITY_KEY))
  const [organizationId] = useState(() => localStorage.getItem(PLAID_ORG_KEY))
  const [isUpdateMode] = useState(() => localStorage.getItem(PLAID_UPDATE_MODE_KEY) === 'true')
  const [itemId] = useState(() => localStorage.getItem(PLAID_ITEM_ID_KEY))

  /** Navigate back to the facility banking page or fallback to banking dashboard */
  const navigateBack = useCallback(() => {
    if (facilityId) {
      navigate(`/settings/facilities/${facilityId}/banking`, { replace: true })
    } else {
      navigate('/banking/dashboard', { replace: true })
    }
  }, [facilityId, navigate])

  /**
   * Handle successful Plaid Link completion.
   * In initial connect mode: exchanges the public token for a permanent access token.
   * In update mode: calls complete-update to reset item status (no token exchange needed).
   *
   * @param public_token - Short-lived token returned by Plaid on success
   * @param metadata - Institution details and account info from Plaid
   */
  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    (public_token, metadata) => {
      if (isUpdateMode && itemId) {
        // Update mode — no token exchange, just clear the error state
        completeUpdate.mutate(
          { itemId },
          {
            onSuccess: () => {
              clearPlaidOAuthState()
              toast.success('Bank reconnected successfully')
              navigateBack()
            },
            onError: (err) => {
              const message = err instanceof Error ? err.message : 'Failed to complete reconnection'
              toast.error(`Bank reconnection failed: ${message}`)
              clearPlaidOAuthState()
              navigateBack()
            },
          }
        )
      } else {
        // Initial connect mode — exchange public token for access token
        connectBank.mutate(
          {
            publicToken: public_token,
            institutionId: metadata.institution?.institution_id || null,
            institutionName: metadata.institution?.name || null,
            organizationId: organizationId || undefined,
            facilityId: facilityId || undefined,
          },
          {
            onSuccess: () => {
              clearPlaidOAuthState()
              navigateBack()
            },
            onError: (err) => {
              const message = err instanceof Error ? err.message : 'Failed to connect bank'
              toast.error(`Bank connection failed: ${message}`)
              clearPlaidOAuthState()
              navigateBack()
            },
          }
        )
      }
    },
    [connectBank, completeUpdate, isUpdateMode, itemId, organizationId, facilityId, navigateBack]
  )

  /**
   * Handle Plaid Link exit — user cancelled or a specific error occurred.
   *
   * Three cases are handled explicitly:
   * - INVALID_LINK_TOKEN: The link token expired during OAuth (4 h limit). Show an
   *   actionable error state so the user can restart the flow from the facility page.
   * - OAUTH_STATE_ID_ALREADY_PROCESSED: User hit the browser back button after OAuth
   *   already completed successfully. Not a real error — clean up silently and show an
   *   informational toast instead.
   * - All other errors: Log to console, clean up, and navigate back as before.
   *
   * @param exitError - Plaid error object if the exit was due to an error, null otherwise
   */
  const onExit = useCallback<PlaidLinkOnExit>(
    (exitError) => {
      if (exitError) {
        if (exitError.error_code === 'INVALID_LINK_TOKEN') {
          clearPlaidOAuthState()
          setError(
            'Your bank connection session has expired. Please return to the facility page and try again.'
          )
          return
        }

        if (exitError.error_code === 'OAUTH_STATE_ID_ALREADY_PROCESSED') {
          // User hit browser back after OAuth already completed — not a real error.
          // Silent redirect without confusing toast message.
          clearPlaidOAuthState()
          navigateBack()
          return
        }

        console.error('Plaid OAuth error:', exitError)
      }
      clearPlaidOAuthState()
      navigateBack()
    },
    [navigateBack]
  )

  const { open, ready } = usePlaidLink({
    token: linkToken,
    receivedRedirectUri: window.location.href,
    onSuccess,
    onExit,
  })

  // Auto-open Plaid Link as soon as the SDK signals it is ready.
  useEffect(() => {
    if (ready) {
      open()
    }
  }, [ready, open])

  // Guard against the Plaid SDK silently failing to initialize.
  // Skip the timeout entirely once ready is true to avoid a race condition.
  useEffect(() => {
    if (!linkToken || ready) return

    const timerId = setTimeout(() => {
      setError('Failed to initialize bank connection. Please try again.')
    }, SDK_INIT_TIMEOUT_MS)

    return () => clearTimeout(timerId)
  }, [ready, linkToken])

  // Show an error state immediately if no link token is present in localStorage.
  useEffect(() => {
    if (!linkToken) {
      setError('No Plaid session found. Please try connecting your bank again.')
    }
  }, [linkToken])

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="max-w-sm text-center text-sm text-destructive">{error}</p>
        <Button variant="default" onClick={navigateBack}>
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">Connecting to your bank...</p>
    </div>
  )
}
